import { datadogLogs } from '@datadog/browser-logs';
import { LOGIN, LOGIN_CONFIRM } from '@/Router/routes';
import { createAuthUrl } from '@/utils/auth';
import ValidationError, { FieldError } from './ValidationError';
import { getProcessEnvUrl } from './getUrl';
import { bootstrapKeycloakStore, keycloakStore } from '../hooks/zustandStore';

const ignoredEndpoints = [
  '/client/loans/latest', // CWT-6087
  '/client/loans/latest/debt-payment-schedule',
];

export async function resolveIsLegacyLogin(): Promise<boolean> {
  const state = keycloakStore.getState();

  if (state.isLegacyLogin === null || state.isLegacyLoginLoading) {
    await bootstrapKeycloakStore();
  }

  return keycloakStore.getState().isLegacyLogin ?? true;
}

async function fetchData(
  path: string,
  customOptions?: RequestInit,
  params?: Record<string, string | number | boolean | undefined>,
) {
  const defaultHeaders: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };

  const isLegacyLogin = await resolveIsLegacyLogin();

  const options: RequestInit = {
    ...customOptions,
    headers: {
      ...defaultHeaders,
      ...customOptions?.headers,
    },
  };

  /* This will parse the params object to query parameters to format parameter=value, removing undefined fields.
  // Filter method removes potential undefined fields, but for some reasons the function returns [string]: string | undefined.
  // For that reason there is this ugly type assertion
  */

  const searchString =
    params &&
    new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v)) as Record<
        string,
        string
      >,
    );

  const formattedParams = searchString ? `?${searchString}` : '';
  const token = sessionStorage.getItem('token');

  if (isLegacyLogin && token) {
    (
      options.headers as Record<string, string>
    ).Authorization = `Basic ${token}`;
  }

  if (!isLegacyLogin) {
    options.credentials = 'include';
  }

  const baseUrl = isLegacyLogin ? 'LEGACY_LOGIN_API' : 'WEB_API_URL';
  const url = `${getProcessEnvUrl(baseUrl)}${path}${formattedParams}`;

  if (customOptions?.body instanceof FormData) {
    delete (options.headers as Record<string, string>)['Content-Type'];
  }

  const response = await fetch(url, { ...options });

  if (response.status === 401 || response.status === 403) {
    sessionStorage.clear();
    window.location.replace('/');
    // Return undefined during cypress tests when a request hasn't been intercepted in tests
    return undefined;
  }

  if (
    !response.ok &&
    !(response.status === 404 && ignoredEndpoints.includes(path))
  ) {
    try {
      const data = await response.clone().json();

      datadogLogs.logger.error(`request: ${path}`, {
        request_url: url,
        error: data,
      });
    } catch (exception) {
      datadogLogs.logger.error(`request: ${path} response parse`, {
        request_url: url,
        error: exception,
      });
    }
  }

  // 403 status code gets returned when client has been blocked via COLA
  if (response.status === 401 || response.status === 403) {
    sessionStorage.clear();
    if (isLegacyLogin) {
      window.location.replace(`${LOGIN}?sessionExpired=true`);
    } else {
      window.location.replace(createAuthUrl(LOGIN_CONFIRM));
    }
    // Return undefined during cypress tests when a request hasn't been intercepted in tests
    return undefined;
  }

  if (response.status === 400) {
    const { errors }: { errors: FieldError[] } = await response.json();
    throw new ValidationError(errors);
  }

  if (!response.ok) {
    throw new Error(response.status.toString());
  }

  try {
    let data;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    if (options.headers.Accept === 'text/html') {
      data = await response.text();
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
    } else if (options.headers.Accept === 'application/pdf') {
      data = await response.blob();
    } else {
      data = await response.json();
    }
    return data;
  } catch {
    // If response doesn't have content return undefined;
  }
}

export default fetchData;
